// Import Swiper styles
import "swiper/css";
import "swiper/css/a11y";
import "swiper/css/navigation";
import "../../theme/SwiperOverrides.css";

import * as styles from "./BreedCardCarousel.module.scss";

import React, { useCallback, useState } from "react";
import { A11y, Navigation, Virtual } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";

import { useClientSide } from "../../hooks/useClientSide";
import useWindowWidth from "../../hooks/useWindowWidth";
import { Pagination } from "../Pagination/Pagination";
import SimpleBreedCarouselCard from "../SignPostCards/SimpleBreedCarouselCard/SimpleBreedCarouselCard";

export interface BreedCardCarouselProps {
	breeds: Queries.BreedContentDataFragment[];
	navTheme?: string;

	/**Indicates whether it's a full width carousel or if it's in a block  */
	isStandalone?: boolean;
}

export const BreedCardCarousel: React.FC<BreedCardCarouselProps> = ({
	breeds,
	isStandalone = true,
}) => {
	const isClientSide = useClientSide();
	const [swiper, setSwiper] = useState(null);
	const [currentSlide, setCurrentSlide] = useState(0);
	const windowWidth = useWindowWidth();
	const prevSlide = () => swiper?.activeIndex > 0 && swiper?.slidePrev();
	const nextSlide = () =>
		swiper?.activeIndex < breeds.length - 1 && swiper?.slideNext();

	const getVisibleCardCount = useCallback(
		(windowWidth: number) => {
			if (isStandalone) {
				if (windowWidth >= 1200) return 4;
				if (windowWidth >= 740) return 3;
				if (windowWidth >= 600) return 2;
				return 1.5;
			}
			if (windowWidth >= 1024) return 2;
			if (windowWidth >= 740) return 3;
			if (windowWidth >= 600) return 2;
			return 1.5;
		},
		[isStandalone],
	);

	if (!isClientSide) return <div className={styles.swiperwrapper} />;

	return (
		<>
			<div className={styles.swiperwrapper}>
				<Swiper
					key={breeds.length}
					className={styles.swiper}
					modules={[Virtual, Navigation, A11y]}
					edgeSwipeThreshold={10}
					threshold={0}
					grabCursor={true}
					draggable={false}
					resistance={false}
					autoplay={false}
					noSwiping
					allowTouchMove={false}
					onSwiper={(swiper) => setSwiper(swiper)}
					onSlideChange={(swiper) => setCurrentSlide(swiper.activeIndex)}
					initialSlide={0}
					centerInsufficientSlides={true}
					breakpoints={{
						320: {
							slidesPerView: getVisibleCardCount(320) + 0.25,
							enabled: breeds.length > getVisibleCardCount(320),
						},
						600: {
							slidesPerView: getVisibleCardCount(600) + 0.25,
							enabled: breeds.length > getVisibleCardCount(600),
						},
						740: {
							slidesPerView: getVisibleCardCount(740) + 0.25,
							enabled: breeds.length > getVisibleCardCount(740),
						},

						1024: {
							slidesPerView: getVisibleCardCount(1024) + 0.25,
							enabled: breeds.length > getVisibleCardCount(1024),
						},

						1200: {
							slidesPerView: getVisibleCardCount(1200) + 0.25,
							enabled: breeds.length > getVisibleCardCount(1200),
						},
					}}
				>
					{breeds.map((breed) => (
						<SwiperSlide className={styles.cardslide} key={breed.title}>
							<SimpleBreedCarouselCard
								title={breed.title}
								body={breed.field_intro_copy}
								imgAlt={
									breed.relationships.field_featured_image.field_media_image.alt
								}
								imgSrc={
									breed.relationships.field_featured_image.field_media_image.uri
								}
								buttonUrl={breed.path.alias}
							/>
						</SwiperSlide>
					))}
				</Swiper>
				<div className={styles.pagination}>
					<Pagination
						onFirstClick={() => swiper.slideTo(0)}
						onPreviousClick={prevSlide}
						onNextClick={nextSlide}
						onLastClick={() => swiper.slideTo(breeds.length - 1)}
						firstDisabled={currentSlide === 0}
						lastDisabled={
							currentSlide === breeds.length - getVisibleCardCount(windowWidth)
						}
						previousDisabled={currentSlide === 0}
						nextDisabled={
							currentSlide === breeds.length - getVisibleCardCount(windowWidth)
						}
						total={breeds.length}
						position={currentSlide}
						slim={true}
						noOffset={true}
					/>
				</div>
			</div>
		</>
	);
};

export default BreedCardCarousel;
